import {StyleSheet, Text, TouchableOpacity, View} from "react-native";
import {type Href, router} from "expo-router";
import {Bell} from "lucide-react-native";
import {useTranslation} from "react-i18next";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {useUnreadNotificationCount} from "@/data/notification-hooks";

/** Above this the count renders as `99+`; three digits would burst the pill in the header. */
const MAX_SHOWN_COUNT = 99;

/**
 * The dashboard's way into the inbox, with the unread count on it.
 *
 * The count is deliberately org-independent even though the bell lives in an org header: the inbox
 * is one list per user, so switching organization must not change the number.
 */
export const NotificationBell = () => {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const unreadCount = useUnreadNotificationCount();
    const styles = createStyles(theme);

    return (
        <TouchableOpacity
            style={styles.bell}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={t("notifications.title")}
            onPress={() =>
                // cast via unknown: Expo Router's typed-route union only includes this new route
                // after `.expo/types` regenerates on the next `expo start`.
                router.push("/notifications" as unknown as Href)
            }
        >
            <Bell size={16} color={theme.colors.textSecondary}/>
            {!!unreadCount && (
                <View style={styles.badge} accessibilityLabel={t("notifications.unread")}>
                    <Text style={styles.badgeText}>
                        {unreadCount > MAX_SHOWN_COUNT ? `${MAX_SHOWN_COUNT}+` : unreadCount}
                    </Text>
                </View>
            )}
        </TouchableOpacity>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    bell: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
        borderRadius: theme.borderRadius.full,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        gap: 4,
    },

    badge: {
        minWidth: 16,
        paddingHorizontal: 4,
        borderRadius: theme.borderRadius.full,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },

    badgeText: {
        fontSize: theme.fontSize.xs,
        fontWeight: '700',
        color: theme.colors.background,
    },
});
